import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Header from "../main/components/header/header";
import { useDispatch, useSelector } from "react-redux";
import { Toaster } from "react-hot-toast";
import useDashboardMenu from "../../hooks/useDashboardMenu";
import useUserRole from "../../hooks/useUserRole";
import { _userRole } from "../../utils/common";
import PlanExpiryCountdown from "../main/components/common/planExpiryCountdown";
import SideReminder from "../components/reminder/sideReminder";
import BirthdayModal from "../components/modals/birthdayModal";
import moment from "moment";
import { _setIsPlanSheet } from "../../store/slices/global.slice";
import Lottie from "lottie-react";
import mainLoading from "../assets/lottie/main-loading.json";
import QueuePopup from "../components/queue/queuePopup";
import { getItem } from "../../utils/localStorage";
import Sidebar from "../main/components/sidebar/Sidebar";
// UploadPanel removed - using simple photo upload system instead

const MainRoute = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const role = useUserRole();
  const user = useSelector((state) => state?.user?.userInfo?.user);
  const { showUpload } = useSelector((state) => state.global);
  const [backButton, SetBackButton] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isVerifyingAuth, setIsVerifyingAuth] = useState(true);
  const { isAuthenticated, loading, profileLoad } = useSelector((state) => state.user);
  const { settingMenu, dashboardMenu } = useDashboardMenu();

  // Initial authentication check on mount
  useEffect(() => {
    const token = getItem("user")?.token;
    
    // If no token, redirect immediately
    if (!token) {
      navigate("/auth/login", { replace: true });
      setIsVerifyingAuth(false);
      return;
    }

    // If token exists, we need to verify it (even if isAuthenticated is true from persisted state)
    // App.jsx will call _fetchUserData() to verify the token
    // We'll mark verification complete when profileLoad finishes
    // Don't set isVerifyingAuth to false here - wait for profileLoad to complete
  }, []); // Run only once on mount

  // Monitor authentication state and profile loading
  useEffect(() => {
    const token = getItem("user")?.token;
    
    // If no token, redirect
    if (!token) {
      navigate("/auth/login", { replace: true });
      setIsVerifyingAuth(false);
      return;
    }

    // If profile loading is complete
    if (!profileLoad) {
      // If authenticated, verification complete
      if (isAuthenticated) {
        setIsVerifyingAuth(false);
      } else {
        // Token exists but user not authenticated after profile load
        // This means token is invalid
        navigate("/auth/login", { replace: true });
        setIsVerifyingAuth(false);
      }
    }
  }, [profileLoad, isAuthenticated, navigate]);

  useEffect(() => {
    const locationArray = location.pathname.split("/");
    if (locationArray.length > 2) {
      SetBackButton(true);
    } else {
      SetBackButton(false);
    }
  }, [location.pathname]);

  const loadInitialData = useCallback(() => {
    if (!isAuthenticated) return;

    const isWorkspaceAdmin = role === _userRole.WORKSPACE_ADMIN;
    if (!isWorkspaceAdmin) return;
    // Removed package-related functionality
  }, [isAuthenticated, role, dispatch]);

  useEffect(() => {
    if (!user) return;
    if (user?.user_info?.dob) {
      const today = moment().format("MM-DD");
      const givenDate = moment(user?.user_info?.dob).format("MM-DD");
      if (today === givenDate) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    }
  }, [user]);

  // Show loading while authentication is being verified
  // Show loading if:
  // 1. Redux loading is true, OR
  // 2. Profile is being loaded (verifying token), OR
  // 3. We're still verifying authentication
  if (loading || profileLoad || isVerifyingAuth) {
    return (
      <div className="flex flex-col justify-center items-center h-screen text-center">
        <Lottie animationData={mainLoading} loop={true} className="h-[400px] text-theme" />
        <span className="relative -top-24 text-sm font-bold text-primary-900">
          Getting things ready for you...
        </span>
      </div>
    );
  }

  // Final check: if not authenticated after verification, show loading while redirecting
  const token = getItem("user")?.token;
  if (!token || !isAuthenticated) {
    return (
      <div className="flex flex-col justify-center items-center h-screen text-center">
        <Lottie animationData={mainLoading} loop={true} className="h-[400px] text-theme" />
        <span className="relative -top-24 text-sm font-bold text-primary-900">
          Redirecting to login...
        </span>
      </div>
    );
  }

  const onClose = () => {
    setIsOpen(false);
  };

  const handleClose = () => {
    dispatch(_setIsPlanSheet(false));
  };

  return (
    <div className="flex flex-row w-screen h-screen text-theme text-cs overflow-hidden">
      <Sidebar menu={dashboardMenu} settingmenu={settingMenu} />

      <div className="flex flex-col flex-1 min-w-0 h-screen overflow-y-auto relative">
        <Header backButton={backButton} navigate={navigate} menu={[]} />

        <div className="w-full bg-white">
          <Outlet />
        </div>
        <div className="sticky w-full hidden">
          <PlanExpiryCountdown expiryDate="2025-3-31" />
          <SideReminder />
        </div>
        <BirthdayModal isOpen={isOpen} onCancel={onClose} />

        {showUpload ? <QueuePopup show={showUpload} /> : null}
      </div>
    </div>
  );
};

export default MainRoute;
